import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CreditsCostGrid } from './CreditsCostGrid';
import { CreditsRing } from './CreditsRing';
import { CreditsSimulator } from './CreditsSimulator';
import { CreditsPacks } from './CreditsPacks';
import axios from 'axios';
import { recharger } from './Credits40';
import { toast } from 'sonner';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { X, Loader2, Check, History } from 'lucide-react';
import { getApiUrl } from '../../utils/api';
import './credits.css';

export const openCredits = () => window.dispatchEvent(new CustomEvent('open-credits'));



// Intercepteur global : toute action refusée faute de crédits ouvrez la boutique
if (!window.__creditsInterceptor) {
  window.__creditsInterceptor = true;
  axios.interceptors.response.use((r) => {
    const c = r?.data?.charged;
    if (typeof c === 'number' && c > 0) {
      const b = r.data.balance;
      toast(`−${c.toLocaleString('fr-FR')} ★${typeof b === 'number' ? ` · il vous reste ${b.toLocaleString('fr-FR')}` : ''}`, { description: `${r.data.agent_nom || 'Sabril'} s’est mis au travail.`, duration: 3500 });
      window.dispatchEvent(new CustomEvent('credits-changed'));
    }
    return r;
  }, (err) => {
    const d = err?.response?.data?.detail;
    if (err?.response?.status === 402 && d?.code === 'credits_insufficient') {
      toast.error(`Crédits insuffisants : ${d.needed} nécessaires pour « ${d.label} », solde ${d.balance}.`);
      openCredits();
    }
    return Promise.reject(err);
  });
}

// Pastille « Crédits » de l'en-tête (style Emergent) + modal packs / coûts / historique
export const CreditsPill = () => {
  const API = getApiUrl();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [ledger, setLedger] = useState(null);
  const [busy, setBusy] = useState('');
  const [selected, setSelected] = useState(null);
  const [basket, setBasket] = useState({});
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const basePath = tenantSlug ? `/${tenantSlug}` : '';
  const load = useCallback(() => axios.get(`${API}/credits/balance`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => {}), [API]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onOpen = () => setOpen(true); const onRefresh = () => load();
    window.addEventListener('open-credits', onOpen); window.addEventListener('credits-changed', onRefresh);
    return () => { window.removeEventListener('open-credits', onOpen); window.removeEventListener('credits-changed', onRefresh); };
  }, [load]);
  useEffect(() => {
    const sid = sp.get('credits_session');
    if (!sid) { if (sp.get('credits_canceled')) { toast.info('Achat annulé.'); const n = new URLSearchParams(sp); n.delete('credits_canceled'); setSp(n, { replace: true }); } return; }
    axios.get(`${API}/credits/confirm`, { params: { session_id: sid }, withCredentials: true })
      .then((r) => { if (r.data.paid) { toast.success(`+${r.data.credits} crédits ajoutés — solde ${r.data.balance}`); load(); window.dispatchEvent(new CustomEvent('credits-changed')); } else toast.error('Paiement non confirmé'); })
      .catch(() => toast.error('Confirmation impossible')).finally(() => { const n = new URLSearchParams(sp); n.delete('credits_session'); setSp(n, { replace: true }); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) axios.get(`${API}/credits/ledger`, { params: { limit: 60 }, withCredentials: true }).then((r) => setLedger(r.data.ledger)).catch(() => {}); }, [open, API]);
  if (!data) return null;
  const daysTitle = data.free ? 'Crédits illimités' : data.days_left != null ? `${data.balance} crédits · environ ${data.per_day} par jour, soit ${data.days_left} jour${data.days_left > 1 ? 's' : ''} restant${data.days_left > 1 ? 's' : ''}. Clic : détail par usage, historique, recharger.` : `${data.balance} crédits · aucune consommation sur 30 jours. Clic : détail et recharge.`;
  const buy = async (pack) => {
    if (pack?.key !== 'custom') { setOpen(false); recharger({}); return; }  // LOT 54 · un seul parcours de recharge (3 packs, devise de l'établissement)
    setBusy(pack.key);
    try {
      const r = await axios.post(`${API}/credits/checkout`, { pack: pack.key, amount_eur: pack.amount_eur, origin_url: window.location.origin, return_path: window.location.pathname + window.location.search }, { withCredentials: true });
      if (r.data.url) window.location.assign(r.data.url);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Achat indisponible'); setBusy(''); }
  };
  const month = new Date().toISOString().slice(0, 7);
  const used = (ledger || []).filter((l) => l.delta < 0 && (l.at || '').startsWith(month)).reduce((a, l) => a - l.delta, 0);
  const pack = data.packs.find((p) => p.key === selected);
  const reco = used > data.balance ? (data.packs.find((p) => data.balance + p.credits + p.bonus >= used * 1.5) || data.packs[data.packs.length - 1]) : null;
  const addToBasket = (k) => setBasket((b) => ({ ...b, [k]: (b[k] || 0) + 1 }));
  const canDo = Object.entries(data.costs).filter(([k]) => k.startsWith('boost_')).map(([k, c]) => [c.label, Math.floor(data.balance / c.credits)]).filter(([, n]) => n > 0).slice(0, 4);
  return <>
    <button type="button" className={`credits-pill ${data.alert ? 'is-alert' : ''}`} onClick={() => navigate(`${basePath}/credits`)} title={daysTitle} aria-label={`Crédits : ${data.balance}. ${daysTitle}`} data-testid="credits-pill">
      <svg className="credits-pill-star" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M12 1.5l2.7 7.8 7.8 2.7-7.8 2.7L12 22.5l-2.7-7.8-7.8-2.7 7.8-2.7z" fill="#1C1714" /></svg>
      <span className="credits-pill-value" data-testid="credits-balance">{data.free ? '∞' : data.balance.toLocaleString('fr-FR')}</span>
    </button>
    {open && createPortal(
      <div className="fixed inset-0 z-[150] bg-black/55 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setOpen(false)} data-testid="credits-modal">
        <div className="credits-modal" onClick={(e) => e.stopPropagation()}>
          <header className="credits-head">
            <CreditsRing balance={data.balance} used={used} preview={pack ? pack.credits + pack.bonus : 0} free={data.free} />
            <div className="min-w-0 flex-1">
              <span className="widget-kicker">Vos crédits</span>
              <h3 data-testid="credits-modal-balance">{data.free ? 'Illimités' : `${data.balance.toLocaleString('fr-FR')} crédit${data.balance > 1 ? 's' : ''}`}</h3>
              <p>Abonnement {data.subscription.label} · {data.subscription.price_eur_month} € HT/mois · <b>{data.subscription.credits_month.toLocaleString('fr-FR')} crédits inclus chaque mois</b>. Chaque action de Sabril affiche son coût en crédits avant de se lancer.</p>
              {!data.free && canDo.length > 0 && <p className="credits-cando" data-testid="credits-cando">Avec votre solde, vous pouvez encore lancer : {canDo.map(([l, n], i) => <span key={l}>{i ? ' · ' : ''}<b>{n}×</b> {l}</span>)}</p>}
            </div>
            <button type="button" className="sab-icon" onClick={() => setOpen(false)} aria-label="Fermer" data-testid="credits-close"><X size={16} /></button>
          </header>
          <div className="credits-body">
            <CreditsPacks data={data} selected={selected} onSelect={setSelected} busy={busy} onBuy={buy} reco={reco} />
            <CreditsSimulator costs={data.costs} basket={basket} setBasket={setBasket} balance={data.balance} packs={data.packs} selected={selected} onSelectPack={setSelected} free={data.free} />
            <div className="credits-cols">
              <section className="is-costs"><h4>Ce que coûte chaque action</h4><CreditsCostGrid costs={data.costs} onAdd={addToBasket} basket={basket} /></section>
              <section><h4><History size={13} /> Derniers mouvements{used > 0 && <span className="credits-used" data-testid="credits-used-month">{used.toLocaleString('fr-FR')} utilisés ce mois</span>}</h4>{!ledger ? <p className="credits-empty"><Loader2 size={13} className="animate-spin" /></p> : !ledger.length ? <p className="credits-empty">Aucun mouvement pour l’instant.</p> : <ul data-testid="credits-ledger">{ledger.slice(0, 12).map((l) => <li key={l.ledger_id}><span>{l.label}<small>{new Date(l.at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</small></span><b className={l.delta > 0 ? 'is-plus' : ''}>{l.delta > 0 ? '+' : ''}{l.delta.toLocaleString('fr-FR')}</b></li>)}</ul>}</section>
            </div>
            <p className="credits-foot"><Check size={12} /> Paiement sécurisé Stripe · crédits sans date d’expiration · factures dans Mon abonnement</p>
          </div>
        </div>
      </div>, document.body)}
  </>;
};
